import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Auth Provider
import { AuthProvider } from "./context/AuthContext";
import InstantOrderAlertsProvider from "./context/InstantOrderAlertsContext";
import ProtectedRoute from "./components/common/ProtectedRoute";

// Pages
import Login from "./pages/auth/Login";
import Dashboard from "./pages/dashboard/Dashboard";
import Orders from "./pages/orders/Orders";
import Users from "./pages/users/Users";
import UserDetail from "./pages/users/UserDetail";
import Subscriptions from "./pages/subscriptions/Subscriptions";
import SubscriptionDetail from "./pages/subscriptions/SubscriptionDetail";
import EditSubscription from "./pages/subscriptions/EditSubscription";
import Plans from "./pages/plans/Plans";
import DeliveryAgents from "./pages/deliveryAgents/DeliveryAgents";
import RiderRequests from "./pages/deliveryAgents/RiderRequests";
import LiveRiderMap from "./pages/deliveryAgents/LiveRiderMap";
import { Discounts } from "./pages/discounts";
import DiscountDetail from "./pages/discounts/DiscountDetail";
import CreateDiscount from "./pages/discounts/CreateDiscount";
import EditDiscount from "./pages/discounts/EditDiscount";
import Meals from "./pages/meals/Meals";
import Drinks from "./pages/drinks/Drinks";
import RequestedAreas from "./pages/requestedAreas/RequestedAreas";
import ServiceAreasMap from "./pages/serviceAreas";
import SwapSets from "./pages/swapSets/SwapSets";
import SwapSetDetail from "./pages/swapSets/SwapSetDetail";
import Holidays from "./pages/holidays/Holidays";
import Feedbacks from "./pages/feedbacks/Feedbacks";
import Kitchens from "./pages/kitchens/Kitchens";
import Stocks from "./pages/stocks/Stocks";
import Analytics from "./pages/analytics/Analytics";
import KitchenRequirementsPage from "./pages/kitchenRequirements/KitchenRequirementsPage";

// Instant Pages
import ItemCategories from "./pages/kinnow/ItemCategories";
import NowMeals from "./pages/kinnow/NowMeals";
import DiscoveryBadges from "./pages/kinnow/DiscoveryBadges";
import CreateNowMeal from "./pages/kinnow/CreateNowMeal";
import EditNowMeal from "./pages/kinnow/EditNowMeal";
import SharedStockGroups from "./pages/kinnow/SharedStockGroups";
import InstantOrders from "./pages/kinnow/InstantOrders";
import InstantOrdersQueue from "./pages/kinnow/InstantOrdersQueue";
import SideItems from "./pages/kinnow/SideItems";
import NowCombos from "./pages/kinnow/NowCombos";
import UserManagement from "./pages/admins/UserManagement";
import PayslipGenerator from "./pages/payslip/PayslipGenerator";

const theme = createTheme({
  palette: {
    primary: {
      main: "#f97316", // Kinnow Orange
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#0f172a", // Slate Dark
      contrastText: "#ffffff",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#475569",
    },
  },
  typography: {
    fontFamily: [
      "Plus Jakarta Sans",
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
    body1: { fontSize: "0.875rem", lineHeight: 1.5 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    button: { textTransform: "none", fontWeight: 600, fontSize: "0.95rem" },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "none",
          padding: "8px 20px",
          "&:hover": {
            boxShadow: "none",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #ea580c 0%, #d97706 100%)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#f1f5f9",
          "& .MuiTableCell-root": {
            color: "#475569",
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.75rem",
            letterSpacing: "0.05em",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: "#f1f5f9",
          padding: "12px 16px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.875rem",
        },
      },
    },
  },
});


const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <InstantOrderAlertsProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredPermission="analytics">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute requiredPermission="orders">
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedbacks"
              element={
                <ProtectedRoute requiredPermission="orders">
                  <Feedbacks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute requiredPermission="subscriptions">
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions/:subscriptionId"
              element={
                <ProtectedRoute requiredPermission="subscriptions">
                  <SubscriptionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions/:subscriptionId/edit"
              element={
                <ProtectedRoute requiredPermission="subscriptions">
                  <EditSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans"
              element={
                <ProtectedRoute requiredPermission="subscriptions">
                  <Plans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredPermission="users">
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <ProtectedRoute requiredPermission="users">
                  <UserDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deliveryAgents"
              element={
                <ProtectedRoute requiredPermission="deliveryAgents">
                  <DeliveryAgents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rider-requests"
              element={
                <ProtectedRoute requiredPermission="deliveryAgents">
                  <RiderRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-map"
              element={
                <ProtectedRoute requiredPermission="deliveryAgents">
                  <LiveRiderMap />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admins"
              element={
                <ProtectedRoute requiredPermission="admins">
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts"
              element={
                <ProtectedRoute requiredPermission="discounts">
                  <Discounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts/create"
              element={
                <ProtectedRoute requiredPermission="discounts">
                  <CreateDiscount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts/:id"
              element={
                <ProtectedRoute requiredPermission="discounts">
                  <DiscountDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts/:id/edit"
              element={
                <ProtectedRoute requiredPermission="discounts">
                  <EditDiscount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meals"
              element={
                <ProtectedRoute requiredPermission="meals">
                  <Meals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drinks"
              element={
                <ProtectedRoute requiredPermission="drinks">
                  <Drinks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/swap-sets"
              element={
                <ProtectedRoute requiredPermission="meals">
                  <SwapSets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/swap-sets/:id"
              element={
                <ProtectedRoute requiredPermission="meals">
                  <SwapSetDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requested-areas"
              element={
                <ProtectedRoute requiredPermission="analytics">
                  <RequestedAreas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/service-areas-map"
              element={
                <ProtectedRoute requiredPermission="analytics">
                  <ServiceAreasMap />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holidays"
              element={
                <ProtectedRoute requiredPermission="holidays">
                  <Holidays />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kitchens"
              element={
                <ProtectedRoute requiredPermission="kitchens">
                  <Kitchens />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stocks"
              element={
                <ProtectedRoute requiredPermission="kitchens">
                  <Stocks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kitchen-requirements"
              element={
                <ProtectedRoute requiredPermission="orders">
                  <KitchenRequirementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute requiredPermission="analytics">
                  <Analytics />
                </ProtectedRoute>
              }
            />


            {/* Instant Routes */}
            <Route
              path="/instant-orders"
              element={
                <ProtectedRoute requiredPermission="instantOrders">
                  <InstantOrdersQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instant-orders/history"
              element={
                <ProtectedRoute requiredPermission="instantOrders">
                  <InstantOrders />
                </ProtectedRoute>
              }
            />

            <Route
              path="/item-categories"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <ItemCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discovery-badges"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <DiscoveryBadges />
                </ProtectedRoute>
              }
            />
            <Route
              path="/side-items"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <SideItems />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shared-stock-groups"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <SharedStockGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path="/now-meals"
              element={
                <ProtectedRoute requiredPermission="instantMenu">
                  <NowMeals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/now-meals/create"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <CreateNowMeal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/now-meals/:id/edit"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <EditNowMeal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/now-combos"
              element={
                <ProtectedRoute requiredPermission="nowMeals">
                  <NowCombos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payslip-generator"
              element={
                <ProtectedRoute requiredPermission="admins">
                  <PayslipGenerator />
                </ProtectedRoute>
              }
            />

            {/* Redirect to dashboard if already logged in, otherwise to login */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
        </InstantOrderAlertsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
